import React from 'react';
import propTypes from 'prop-types';
import moment from 'moment';

import {
  AppBar,
  CssBaseline,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Toolbar,
  IconButton,
  TextField,
  Typography,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { BiCodeBlock } from 'react-icons/bi';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import AssignmentIcon from '@material-ui/icons/Assignment';
import InfoIcon from '@material-ui/icons/Info';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import StopIcon from '@material-ui/icons/Stop';
import ParticipantButtonWithBadge from '../ExternalVideoComponent/ParticipantButtonWithBadge';
import frontEndConfigFile from '../../configFiles/frontendConfigFile';
import { codeFrameCodeShareUrlForModerators, codeFrameURL, frontendUrl, publicJoinUrl } from '../../configFiles/config';
import './moderatorTopNavBar.css';
import myAnatomyLogo from '../../images/myAnatomyLogo.png';
import { defaultParticipantName } from '../../configFiles/config';
import Loader from '../Loader';
import { showSuccessToast } from '../../utils/toasts';
import CastForEducationIcon from '@material-ui/icons/CastForEducation';
import { theme } from '../../utils/uiTheme';

const getRecordingUrl = (gcsUrl) => {
  if (!gcsUrl) {
    return gcsUrl;
  }

  if (gcsUrl.startsWith('gs://')) {
    const path = gcsUrl.replace('gs://', '');
    return `https://storage.cloud.google.com/${path}`;
  }

  return gcsUrl;
};

class ModeratorTopNavBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showModeratorOptionsDialog: false,
      showParticipantsBoardDialogModal: false,
      openedCodeShareWindows: [],
      openedNotesWindow: [],
      openedWhiteboardWindow: [],
      showMeetingDetailsDialog: false,
      showMeetingDetailsDialog: false,
      showRecordingConfirmation: false,
      recordingAction: null, // 'START' or 'STOP'
      isRecordingActionLoading: false,
    };
  }

  componentDidUpdate(prevProps) {
    const prevIsRecording = prevProps.recordingStatus && prevProps.recordingStatus.isRecording === true;
    const currentRecordingStatus = this.props.recordingStatus || {};
    const currentIsRecording = currentRecordingStatus.isRecording === true;
    const currentStatusValue = currentRecordingStatus.status;

    if (prevIsRecording && !currentIsRecording && currentStatusValue === 'COMPLETED') {
      showSuccessToast('Recording has been stopped');
    }
  }

  handleModeratorExit = () => {
    const { recordingStatus, stopRecording, meetingId } = this.props;

    const isRecording = recordingStatus?.isRecording === true;

    if (isRecording) {
      try {
        stopRecording(meetingId);
        showSuccessToast('Recording is stopped');
      } catch (err) {
        console.error('Failed to stop recording on exit', err);
      }
    }
  };

  toggleRecording = () => {
    const { recordingStatus } = this.props;
    const isRecording = recordingStatus?.isRecording === true;

    this.setState({
      showRecordingConfirmation: true,
      recordingAction: isRecording ? 'STOP' : 'START',
    });
  }

  confirmRecordingAction = async () => {
    const { recordingAction } = this.state;
    const { startRecording, stopRecording, getRecordingStatus, meetingId } = this.props;

    this.setState({
      isRecordingActionLoading: true,
      showRecordingConfirmation: false,
      recordingUiLock: recordingAction,
      recordingUiLockSetAtMs: Date.now(),
    });

    try {
      if (recordingAction === 'START') {
        await Promise.resolve(startRecording(meetingId));
      } else {
        await Promise.resolve(stopRecording(meetingId));
      }

      await new Promise((resolve) => setTimeout(resolve, 2000));
      await Promise.resolve(getRecordingStatus(meetingId));
    } finally {
      this.setState({ isRecordingActionLoading: false });
    }
  }

  cancelRecordingAction = () => {
    this.setState({ showRecordingConfirmation: false });
  }

  openMeetingDetailsDialog = () => {
    this.setState({ showMeetingDetailsDialog: true });
  }

  closeMeetingDetailsDialog = () => {
    this.setState({ showMeetingDetailsDialog: false });
  }


  componentDidMount() {
    window.addEventListener('beforeunload', this.closeEventForCodeShareBoard);
    window.addEventListener('beforeunload', this.closeEventForNotesBoard);
    window.addEventListener('beforeunload', this.closeEventForWhiteboardBoard);

    window.addEventListener('beforeunload', this.handleModeratorExit);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.closeEventForCodeShareBoard);
    window.removeEventListener('beforeunload', this.closeEventForNotesBoard);
    window.removeEventListener('beforeunload', this.closeEventForWhiteboardBoard);

    window.removeEventListener('beforeunload', this.handleModeratorExit);

    // also handle SPA navigation
    this.handleModeratorExit();
  }

  closeEventForWhiteboardBoard = () => {
    const { openedWhiteboardWindow } = this.state;

    openedWhiteboardWindow.forEach((board) => {
      board.close();
    }
    );
    this.setState({
      openedWhiteboardWindow: [],
    });
  }

  closeEventForNotesBoard = () => {
    const { openedNotesWindow } = this.state;

    openedNotesWindow.forEach((board) => {
      board.close();
    }
    );

    this.setState({
      openedNotesWindow: [],
    });
  }


  closeEventForCodeShareBoard = () => {
    const {
      openedCodeShareWindows,
    } = this.state;

    openedCodeShareWindows.forEach(board => {
      board.close();
    });

    this.setState({
      openedCodeShareWindows: [],
    });
  }

  showCodeShareBoard = (participantName, participantToken) => {
    const {
      token,
      meetingId,
      externalMeetingId,
    } = this.props;
    const {
      openedCodeShareWindows,
    } = this.state;
    let windowHeight = 440;
    let windowWidth = 630;

    let adjustFromTop = 0;

    if (window.screen) {
      windowHeight = (window.screen.availHeight * 70) / 100;
      windowWidth = (window.screen.availWidth);
      adjustFromTop = (window.screen.availHeight * 15) / 100;
    }

    let codeShareURL = codeFrameCodeShareUrlForModerators;

    codeShareURL = codeShareURL.replace('<<meetingId>>', meetingId);
    codeShareURL = codeShareURL.replace('<<meetingToken>>', token);
    codeShareURL = codeShareURL.replace('<<participantToken>>', participantToken);
    codeShareURL = codeShareURL.replace('<<externalMeetingId>>', externalMeetingId);
    codeShareURL = codeShareURL.replace('<<participantName>>', participantName);
    const openWindow = window.open(
      codeShareURL,
      '_blank',
      `
          width=${windowWidth},
          height=${windowHeight},
          top=${adjustFromTop},
          location=no,
        `
    );
    openedCodeShareWindows.push(openWindow);
    this.setState({
      openedCodeShareWindows,
    });
  }

  openParticipantsBoardDetailsDialog = (meetingId) => {
    const {
      fetchParticipantsSharingBoard,
    } = this.props;
    this.setState(() => ({
      showParticipantsBoardDialogModal: true,
    }));
    fetchParticipantsSharingBoard(meetingId);
  }

  closeParticipantsBoardDetailsDialog = () => {
    this.setState(() => ({
      showParticipantsBoardDialogModal: false,
    }));
  }

  openModeratorOptionsDialog = () => {
    this.setState(() => ({
      showModeratorOptionsDialog: true,
    }));
  }

  closeModeratorOptionsDialog = () => {
    this.setState(() => ({
      showModeratorOptionsDialog: false,
    }));
  }

  getMeetingSubmissions = () => {
    const {
      getSubmissionsForMeeting,
    } = this.props;

    getSubmissionsForMeeting();
  }

  downloadMeetingContentHistory = () => {
    const {
      meetingContentHistory,
    } = this.props;

    let finalTextToDownload = '';

    meetingContentHistory && meetingContentHistory.forEach((contentRecord) => {
      const clientDetails = contentRecord.clientDetails;
      const name = clientDetails.name || defaultParticipantName;

      finalTextToDownload += `Participant Name: ${name}\n\n`;

      const usersContentHistory = contentRecord.contentHistory;
      usersContentHistory && usersContentHistory.forEach((usersContentHistoryRecord) => {
        const createdAt = moment(usersContentHistoryRecord.createdAt).format('DD-MM-YYYY HH:mm:ss');
        const submission = usersContentHistoryRecord.content;
        const {
          code,
          language,
        } = submission;

        finalTextToDownload += `${language}\t\t\t\t${createdAt}\n\n`;
        finalTextToDownload += code;
        finalTextToDownload += '\n\n\n\n';
      });
      finalTextToDownload += '\n\n--------------------------------------------------------------\n\n';
    });

    // const header = '<html xmlns:o=\'urn:schemas-microsoft-com:office:office\' ' +
    // 'xmlns:w=\'urn:schemas-microsoft-com:office:word\' ' +
    // 'xmlns=\'http://www.w3.org/TR/REC-html40\'>' +
    // '<head><meta charset=\'utf-8\'><title>Editor Content</title></head><body>';
    // const footer = '</body></html>';


    var sourceHTML = finalTextToDownload;

    var source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
    var fileDownload = document.createElement('a');
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = `submissions.doc`;
    fileDownload.click();
    document.body.removeChild(fileDownload);
  }

  showNotesView = () => {
    let windowHeight = 440;
    let windowWidth = 630;

    let adjustFromTop = 0;

    const { openedNotesWindow } = this.state;

    if (window.screen) {
      windowHeight = (window.screen.availHeight * 70) / 100;
      windowWidth = (window.screen.availWidth);
      adjustFromTop = (window.screen.availHeight * 15) / 100;
    }

    let codeframeUrl = frontEndConfigFile.CODE_FRAME_URL.frontend;
    codeframeUrl = codeframeUrl.replace(/<<codeFrameConfigUrl>>/g, codeFrameURL);

    let notesViewUrl = frontEndConfigFile.NOTES.url;
    notesViewUrl = notesViewUrl.replace('<<codeFrameUrl>>', codeframeUrl);
    const openWindow = window.open(
      notesViewUrl,
      '_blank',
      `
          width=${windowWidth},
          height=${windowHeight},
          top=${adjustFromTop},
          location=no,
        `
    );
    openedNotesWindow.push(openWindow);
    this.setState({
      openedNotesWindow,
    });
  }

  showWhiteboardView = () => {
    let windowHeight = 440;
    let windowWidth = 630;

    let adjustFromTop = 0;

    const { openedWhiteboardWindow } = this.state;

    if (window.screen) {
      windowHeight = (window.screen.availHeight * 70) / 100;
      windowWidth = (window.screen.availWidth);
      adjustFromTop = (window.screen.availHeight * 15) / 100;
    }
    const { externalMeetingId } = this.props;
    //  * -----Uncomment the below lines after integration when getting purpose and client id and pass the url--- *
    let codeframeUrl = frontEndConfigFile.CODE_FRAME_URL.frontend;
    codeframeUrl = codeframeUrl.replace(/<<codeFrameConfigUrl>>/g, codeFrameURL);

    let whiteBoardUrl = frontEndConfigFile.WHITEBOARD.url;
    whiteBoardUrl = whiteBoardUrl.replace('<<codeFrameUrl>>', codeframeUrl);
    whiteBoardUrl = whiteBoardUrl.replace('<<purposeId>>', externalMeetingId);
    whiteBoardUrl = whiteBoardUrl.replace('<<clientId>>', '');

    const openWindow = window.open(
      whiteBoardUrl,
      '_blank',
      `
            width=${windowWidth},
            height=${windowHeight},
            top=${adjustFromTop},
            location=no,
          `
    );

    openedWhiteboardWindow.push(openWindow);

    this.setState({
      openedWhiteboardWindow,
    });
  }

  render() {
    const {
      showModeratorOptionsDialog,
      showParticipantsBoardDialogModal,
    } = this.state;

    const {
      meetingContentHistory,
      isLoadingContentHistory,
      participantsAccessingCodeShare,
      hasMeetingStarted,
      hasMeetingEnded,
      fetchParticipantsSharingBoard,
      meetingId,
      isFetchingParticipantsSharingBoard,
      meetingTitle,
      // externalMeetingId,
      // externalMeetingId,
      raisedHandCount,
      recordingStatus,
      participantCount = 0,
    } = this.props;

    const {
      showMeetingDetailsDialog,
      showRecordingConfirmation,
      recordingAction,
      isRecordingActionLoading,
    } = this.state;

    const recordingStatusObj = recordingStatus || {};
    const recordingStatusValue = recordingStatusObj.status;
    const isRecording = recordingStatusObj.isRecording === true;
    const isStopping = !isRecording && recordingStatusValue === 'STOPPING';
    const isCompleted = recordingStatusValue === 'COMPLETED';
    const recordingUrl = getRecordingUrl(recordingStatusObj.gcsUrl);

    // Button enable/disable logic based on backend status
    const canStartRecording = !isRecording
      && recordingStatusValue !== 'STOPPING'
      && !recordingStatusObj.isActionPending;

    const canStopRecording = isRecording
      && !recordingStatusObj.isActionPending;
    // Use raisedHandCount or another prop to guess participants? 
    // Wait, the requirement says "Disabled if participant count < 2".
    // We might need to check if we have total participant count available.
    // Assuming for now, if it's a constraint, we need that prop. 
    // Raised hand count isn't enough. ExternalVideoComponent has `ParticipantOverlay` but here we might not have the count directly?
    // Let's check `ParticipantButtonWithBadge` usage. It uses `raisedHandCount`.
    // I'll assume we might not have exact count here easily without more plumbing, BUT
    // the prompt says "Frontend should already receive LiveKit participant join/leave events."
    // and `ExternalVideoComponent` has `ParticipantOverlay`.
    // For now, I will add the button and assume specific participant count logic might be in parent or passed down.
    // Actually, I'll pass `participantCount` from `ExternalVideoComponent` if possible, or just ignore the strictly < 2 check if I can't get it easily right now,
    // BUT the prompt is strict.
    // Let's assume `participantsAccessingCodeShare` is NOT the full list.
    // Checking `ExternalVideoComponent` render again... it passes `toggleParticipants`. 
    // It doesn't seem to pass total participant count to ModeratorTopNavBar.
    // I will add a TO-DO or check if I can get it.
    // For now, I will implement the button and logic based on status.

    const hasFewParticipants = (participantCount ?? 0) < 2;

    const isStartDisabled =
      isRecordingActionLoading
      || hasFewParticipants
      || !canStartRecording;
    const isStopDisabled =
      isRecordingActionLoading
      || !canStopRecording;

    const isRecordingButtonDisabled = isRecording ? isStopDisabled : isStartDisabled;

    const recordingButtonTitle = isRecording
      ? (isStopDisabled
        ? (isRecordingActionLoading ? 'Processing...' : 'Stop Recording')
        : 'Stop Recording')
      : (hasFewParticipants
        ? 'Recording with less than 2 participants is not allowed'
        : (isRecordingActionLoading ? 'Processing...' : 'Start Recording'));
    // Combine frontendUrl and publicJoinUrl, ensuring no double slashes if needed, or just simple concatenation
    let inviteLink = (frontendUrl && publicJoinUrl) ? `${frontendUrl}${publicJoinUrl.replace('<<meetingId>>', meetingId)}` : '';

    let shouldShowSubmission = false;
    meetingContentHistory && meetingContentHistory.forEach(meeting => {
      if (meeting.contentHistory && meeting.contentHistory.length > 0) {
        shouldShowSubmission = true;
      }
    });

    return (
      <div>
        <CssBaseline />
        <AppBar
          className='interviewAppbarHeight'
          style={{
            background: theme.primary1Color,
            // height: '59px',
          }}
          position="fixed"
        >
          <Toolbar style={{ justifyContent: 'space-between', position: 'relative' }}>
            <div className='company'>
              <div className='moderator-company-logo'>
                <a href='https://myanatomy.in/#/home' target='_blank' rel='noopener noreferrer'><img className='navbarLogoWidth' src={myAnatomyLogo} alt='MA' /></a>
              </div>
            </div>

            {/* Centered Meeting Title */}
            <Typography
              variant="h6"
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '22%'
              }}
              title={meetingTitle}
            >
              {meetingTitle}
            </Typography>

            <div style={{ display: 'flex', alignItems: 'center' }}>

              {/* Recording Indicator */}
              {isRecording && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '50px',
                    color: 'red',
                    fontWeight: 'bold',
                  }}
                >
                  <FiberManualRecordIcon style={{ fontSize: '14px', marginRight: '4px' }} />
                  <Typography variant="body2">Rec</Typography>
                </div>
              )}

              {!isRecording && isStopping && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '50px',
                    color: 'orange',
                    fontWeight: 'bold',
                  }}
                >
                  <Typography variant="body2">Stopping...</Typography>
                </div>
              )}

              {!isRecording && isCompleted && recordingUrl && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '45px',
                    color: 'white',
                  }}
                >
                  <IconButton
                    style={{ color: 'white' }}
                    title="View / download recording"
                    component="a"
                    href={recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                  <Typography variant="body2">
                    Recording ready
                  </Typography>
                </div>
              )}

              <div className='moderatorOptions' style={{ marginRight: '10px' }} title={recordingButtonTitle}>
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: isRecording ? '#d32f2f' : 'white',
                    color: isRecording ? 'white' : theme.primary1Color,
                    marginRight: '30px',
                    minWidth: 'unset',
                    padding: '4px 8px',
                    opacity: isRecordingButtonDisabled ? 0.5 : 1,
                    cursor: isRecordingButtonDisabled ? 'not-allowed' : 'pointer',
                  }}
                  onClick={this.toggleRecording}
                  disabled={isRecordingButtonDisabled}
                  title={recordingButtonTitle}
                >
                  {isRecording ? <StopIcon /> : <FiberManualRecordIcon />}
                  <span style={{ marginLeft: '5px' }}>
                    {isRecordingActionLoading ? 'Rec..' : (isRecording ? 'Stop' : 'Rec')}
                  </span>
                </Button>
              </div>
              <div className='moderatorOptions'>
                <IconButton
                  style={{ color: 'white' }}
                  title='Meeting Info'
                  onClick={this.openMeetingDetailsDialog}
                >
                  <InfoIcon />
                </IconButton>
              </div>
              <div className='moderatorOptions'>
                <ParticipantButtonWithBadge
                  style={{ color: 'white' }}
                  title='Participants'
                  onClick={this.props.toggleParticipants}
                  count={raisedHandCount}
                />
              </div>
              <div className='moderatorOptions'>
                <IconButton
                  style={{
                    color: 'white',
                  }}
                  onClick={
                    showModeratorOptionsDialog ?
                      this.closeModeratorOptionsDialog
                      :
                      this.openModeratorOptionsDialog
                  }
                >
                  <SettingsApplicationsIcon />
                </IconButton>
              </div>
              {
                hasMeetingStarted && !hasMeetingEnded ?
                  <div className='codeShareOptionForModerator'>
                    <IconButton
                      style={{
                        color: 'white',
                      }}
                      title='Open code share board'
                      onClick={() => { this.openParticipantsBoardDetailsDialog(meetingId); }}
                    >
                      <BiCodeBlock />
                    </IconButton>
                  </div>
                  :
                  null
              }
              {
                hasMeetingStarted && !hasMeetingEnded ?
                  <div className='notesOptionForModerator'>
                    <IconButton
                      style={{
                        color: 'white',
                      }}
                      title='Take Notes'
                      onClick={() => { this.showNotesView(); }}
                    >
                      <AssignmentIcon />
                    </IconButton>
                  </div>
                  :
                  null
              }
              {
                hasMeetingStarted && !hasMeetingEnded ?
                  <div className='whiteboardOptionForModerator'>
                    <IconButton
                      style={{
                        color: 'white',
                      }}
                      title='Open Whiteboard'
                      onClick={() => { this.showWhiteboardView(); }}
                    >
                      <CastForEducationIcon />
                    </IconButton>
                  </div>
                  :
                  null
              }
            </div>
          </Toolbar>
        </AppBar>
        <Dialog
          open={showModeratorOptionsDialog}
          onClose={this.closeModeratorOptionsDialog}
          fullWidth
        >
          <DialogTitle
            className="moderatorOptionsDialogHeader"
            style={{
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            Moderator options
          </DialogTitle>
          <DialogContent
            style={{
              display: 'flex',
              color: 'black',
              justifyContent: 'center',
              fontSize: '18px',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div className='getMeetingSubmissionButtonsContainer'>
              <Button
                color='primary'
                className='getMeetingSubmissionsButton'
                onClick={this.getMeetingSubmissions}
              >
                {
                  meetingContentHistory && meetingContentHistory.length > 0 ?
                    <div>
                      Reload submissions
                    </div>
                    :
                    <div>
                      Get submissions
                    </div>
                }
              </Button>
              <div
                style={{
                  cursor: (meetingContentHistory && meetingContentHistory.length > 0) ? 'pointer' : 'not-allowed',
                }}
              >
                <IconButton
                  onClick={(meetingContentHistory && meetingContentHistory.length > 0) ?
                    this.downloadMeetingContentHistory : null}
                  disabled={!(meetingContentHistory && meetingContentHistory.length > 0)}
                >
                  <CloudDownloadIcon />
                </IconButton>
              </div>
            </div>
            {
              shouldShowSubmission ?
                <div className='contentDetailsContainer'>
                  {
                    meetingContentHistory.map((contentDetails) => {
                      const clientDetails = contentDetails.clientDetails;
                      const clientId = clientDetails.id;
                      const name = clientDetails.name || defaultParticipantName;

                      const contentHistory = contentDetails.contentHistory;
                      return (
                        <div
                          key={clientId}
                          className={contentHistory && contentHistory.length > 0 ? 'contentDetailsParticipantContainer' : ''}
                        >
                          <div className={contentHistory && contentHistory.length > 0 ? 'contentDetailsParticipantName'
                            : 'hiddenParticipantname'}>
                            Participant : {name}
                          </div>
                          <div className='contentDetailsParticipantSubmissions'>
                            {
                              contentHistory.map((contentRecord, index) => {
                                const contentRecordContent = contentRecord.content;
                                return (
                                  <div
                                    key={index}
                                    className='contentDetailsSubmissionRecordContainer'
                                  >
                                    <div className='contentDetailsContent'>
                                      <div className='contentDetailsMetaDataContainer'>
                                        <div className='contentDetailsLanguageIndicator'>
                                          {contentRecordContent.language}
                                        </div>
                                        <div className='contentDetailsSumittedAt'>
                                          Submitted at:&nbsp;{moment(contentRecord.createdAt).format('DD-MM-YYYY HH:mm:ss')}
                                        </div>
                                      </div>
                                      <div className='contentDetailsCode'>
                                        {contentRecordContent.code}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            }
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
                :
                null
            }
            {
              isLoadingContentHistory ?
                <Loader />
                :
                null
            }
          </DialogContent>
          <DialogActions>
            <button
              onClick={this.closeModeratorOptionsDialog}
              color="primary"
              className="fetchParticipantsBoardDialogButton"
              id='closeModeratorOptionsDialog'
            >
              Close
            </button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showParticipantsBoardDialogModal}
          onClose={this.closeParticipantsBoardDetailsDialog}
          fullWidth
        >
          <DialogTitle
            className='participantsBoardDialogHeader'
          >
            Participant boards
          </DialogTitle>
          <DialogContent
            style={{
              display: 'flex',
              color: 'black',
              justifyContent: 'center',
              fontSize: '18px',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {
              participantsAccessingCodeShare.length > 0 ?
                <div className='participantsDetailsTable'>
                  {participantsAccessingCodeShare.map((participant) => {
                    return (
                      <div
                        key={participant.participantToken}
                        className='participantDetails'
                      >
                        <div className='participantName'>
                          <IconButton
                            style={{
                              color: theme.primary1Color,
                            }}
                          >
                            <PersonIcon className='nonClickablePointer' />
                          </IconButton>&nbsp;{participant.participantName}
                        </div>
                        <div className='participantEmail'>
                          <IconButton
                            style={{
                              color: theme.primary1Color,
                            }}
                          >
                            <MailOutlineIcon className='nonClickablePointer' />
                          </IconButton>&nbsp;{participant.participantEmail}
                        </div>
                        <div className='openParticipantBoard'>
                          <IconButton
                            style={{
                              color: theme.primary1Color,
                            }}
                            onClick={() => {
                              this.showCodeShareBoard(participant.participantName, participant.participantToken);
                            }}
                            title={`Click to open ${participant.participantName} board`}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
                :
                <div className='NoParticipantText'>
                  No participant opened their board.
                </div>
            }
          </DialogContent>
          <DialogActions style={{ paddingRight: '24px' }}>
            <button
              onClick={() => fetchParticipantsSharingBoard(meetingId)}
              className='fetchParticipantsBoardDialogButton'
            >
              Refresh
            </button>
            <button
              onClick={() => this.closeParticipantsBoardDetailsDialog()}
              className='closeParticipantsBoardDialog'
            >
              Cancel
            </button>
          </DialogActions>
          {
            isFetchingParticipantsSharingBoard ?
              <Loader />
              :
              null
          }
        </Dialog>

        <Dialog open={showMeetingDetailsDialog} onClose={this.closeMeetingDetailsDialog} fullWidth maxWidth="sm">
          <DialogTitle>Meeting Details</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
              Meeting Name:
            </Typography>
            <Typography variant="body1" gutterBottom>
              {meetingTitle || 'No Title'}
            </Typography>

            <div style={{ marginTop: '20px' }}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Invite Link:
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={inviteLink}
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                />
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: '10px' }}
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    showSuccessToast('Invite link copied successfully');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={this.closeMeetingDetailsDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showRecordingConfirmation}
          onClose={this.cancelRecordingAction}
        >
          <DialogTitle>
            {recordingAction === 'START' ? 'Start Recording?' : 'Stop Recording?'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              {recordingAction === 'START'
                ? "All participants will be notified. Do you want to start recording?"
                : "Are you sure you want to stop the recording?"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={this.cancelRecordingAction} color="default">
              Cancel
            </Button>
            <Button variant="contained" onClick={this.confirmRecordingAction} color="primary" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

ModeratorTopNavBar.propTypes = {
  getSubmissionsForMeeting: propTypes.func,
  meetingContentHistory: propTypes.array,
  isLoadingContentHistory: propTypes.bool,
  meetingId: propTypes.string,
  token: propTypes.string,
  participantsAccessingCodeShare: propTypes.array,
  hasMeetingStarted: propTypes.bool,
  hasMeetingEnded: propTypes.bool,
  fetchParticipantsSharingBoard: propTypes.func,
  isFetchingParticipantsSharingBoard: propTypes.bool,
  externalMeetingId: propTypes.string,
  toggleParticipants: propTypes.func,
  toggleParticipants: propTypes.func,
  raisedHandCount: propTypes.number,
  participantCount: propTypes.number,
  recordingStatus: propTypes.object,
  startRecording: propTypes.func,
  stopRecording: propTypes.func,
  getRecordingStatus: propTypes.func,
};

ModeratorTopNavBar.defaultProps = {
  isLoadingContentHistory: false,
  participantCount: 0,
};

export default ModeratorTopNavBar;
